import styled from 'styled-components';
import { TypeBodyLarge, TypeBodyLargeStrong } from '../../Typography';
import { mqMediumMin, mqLargeMin } from 'styled/tools/media-queries';

export const ContentContainer = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    ![
      'config',
      'width',
      'height',
      'flexGrow',
      'flexShrink',
      'flexBasis',
      'padding',
    ].includes(prop),
})<{
  // Container layout props
  width?: string;
  height?: string;
  flexGrow?: number;
  flexShrink?: number;
  flexBasis?: string;
  padding?: string;

  // Content/theme config for children
  config?: {
    textColor?: 'snowwhite' | 'seedgreen';
    actionConfig?: {
      gap?: string;
      alignItems?: string;
      flexWrap?: string;
      buttonWidth?: string;
      buttonFlexGrow?: number;
    };
    priceConfig?: {
      gap?: string;
    };
  };
}>`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-24);

  flex-grow: ${({ flexGrow }) => (flexGrow !== undefined ? flexGrow : 1)};
  flex-shrink: ${({ flexShrink }) =>
    flexShrink !== undefined ? flexShrink : 1};
  flex-basis: ${({ flexBasis }) => flexBasis || '50%'};

  height: ${({ height }) => height || 'auto'};
  min-height: 0;
  width: ${({ width }) => width || '100%'};

  padding: ${({ padding }) => padding};
  color: ${({ config }) => {
    const colorValue = config?.textColor;
    if (colorValue === 'snowwhite' || colorValue === 'seedgreen') {
      return `var(--color--${colorValue})`;
    }
    return colorValue || 'var(--color--text--primary)';
  }};
`;

export const ProductInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-16);
  flex: 1;
`;

export const ProductHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
`;

export const ProductPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: var(--spacing-8) var(--spacing-16);
  border-radius: var(--radius--small, 8px);

  @media ${mqMediumMin} {
    padding: var(--spacing-16) var(--spacing-24);
  }

  @media ${mqLargeMin} {
    padding: var(--spacing-24) var(--spacing-32);
  }
  font-size: var(--font-size--small, 12px);
  font-weight: var(--font-weight--medium, 500);
  line-height: var(--line-height--tight, 1.2);
  text-transform: uppercase;
  letter-spacing: var(--letter-spacing--wide, 0.5px);
`;

export const ProductPriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: auto;
`;

export const DiscountLabelContainer = styled.div`
  display: flex;
  justify-content: flex-start;
`;

export const PriceDetailsContainer = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    ![
      'config',
      'textColor',
      'width',
      'height',
      'flexGrow',
      'flexShrink',
      'flexBasis',
      'padding',
    ].includes(prop),
})<{
  config?: {
    gap?: string;
  };
}>`
  display: flex;
  align-items: center;
  gap: ${({ config }) => config?.gap || '6px'};
`;

export const CrossedOutPrice = styled(TypeBodyLarge)`
  text-decoration: line-through;
  color: var(--text-disabled);

  /* Mobile styles */
  --fz: 1rem; /* 16px */
  --fw: 350;
  --lh: 1.4; /* 140% = 22.4px */
  --lts: -0.16px;
  font-style: normal;

  /* Desktop styles */
  @media (min-width: 768px) {
    --fz: 1.25rem; /* 20px */
    --fw: 350;
    --lh: 1.3; /* 130% = 26px */
    --lts: -0.2px;
  }
`;

export const ActionContainer = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    ![
      'config',
      'textColor',
      'width',
      'height',
      'flexGrow',
      'flexShrink',
      'flexBasis',
      'padding',
      'hasBothActions',
    ].includes(prop),
})<{
  config?: {
    gap?: string;
    alignItems?: string;
    flexWrap?: string;
    buttonWidth?: string;
    buttonFlexGrow?: number;
  };
  hasBothActions?: boolean;
}>`
  display: flex;
  align-items: ${({ config }) => config?.alignItems || 'center'};
  gap: ${({ config }) => config?.gap || 'var(--spacing--x3, 24px)'};
  align-self: stretch;
  justify-content: flex-start;
  width: auto;

  /* Global button text behavior */
  #add-to-cart-button,
  #digioh-vs-out-of-stock-cta-button {
    white-space: nowrap !important;
  }

  #learn-more-button {
    white-space: nowrap;
  }

  /* Desktop: side by side */
  @media (min-width: 1025px) {
    flex-direction: row;

    #add-to-cart-button,
    #digioh-vs-out-of-stock-cta-button {
      flex: none;
      min-width: ${({ config }) => config?.buttonWidth || '150px'};
    }

    #learn-more-button {
      flex: none;
      white-space: nowrap;
    }
  }

  /* Mobile: side-by-side */
  @media (max-width: 1024px) {
    flex-direction: row;
    align-items: center;
    gap: ${({ config }) => config?.gap || 'var(--spacing--x2, 16px)'};

    #add-to-cart-button,
    #digioh-vs-out-of-stock-cta-button {
      flex: none;
      min-width: 120px;
    }

    #learn-more-button {
      flex: none;
      white-space: nowrap;
    }
  }

  /* Mobile: small */
  @media (max-width: 400px) {
    #add-to-cart-button,
    #digioh-vs-out-of-stock-cta-button {
      min-width: 110px;
      padding: var(--spacing--x2, 16px);
    }
  }
`;

export const ProductHeading = styled.h2`
  font-family: var(--font-family--heading, 'Inter', sans-serif);
  font-size: var(--font-size--large, 18px);
  font-weight: var(--font-weight--semibold, 600);
  line-height: var(--line-height--tight, 1.2);
  color: inherit;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ProductDescription = styled.p`
  font-family: var(--font-family--body, 'Inter', sans-serif);
  font-size: var(--font-size--small, 14px);
  font-weight: var(--font-weight--normal, 400);
  line-height: var(--line-height--relaxed, 1.5);
  color: inherit;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0.8;
`;

export const ProductPrice = styled.p`
  font-family: var(--font-family--body, 'Inter', sans-serif);
  font-size: var(--font-size--large, 18px);
  font-weight: var(--font-weight--semibold, 600);
  line-height: var(--line-height--tight, 1.2);
  color: inherit;
  margin: 0;
`;

export const LearnMore = styled(TypeBodyLargeStrong).withConfig({
  shouldForwardProp: (prop) => prop !== 'color',
})<{ color?: 'seedgreen' | 'snowwhite' }>`
  color: ${({ color = 'seedgreen' }) => `var(--color--${color})`};
  text-decoration: underline;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;
